/**
 * Routing Timeline
 *
 * Horizontal timeline showing routing steps.
 * Color coded:
 * - Complete: green
 * - Current/My work: blue (highlighted)
 * - Upcoming: gray
 */

export default function RoutingTimeline({ routing, selectedWorkCenters }) {
  if (!routing || routing.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic">No routing data</div>
    );
  }

  // Find current step (first incomplete)
  const currentStepIndex = routing.findIndex((r) => !r.complete);

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2">
      {routing.map((step, idx) => {
        const isComplete = step.complete;
        const isCurrent = idx === currentStepIndex;
        const isMyWork = step.isMyWork;
        const isPast = idx < currentStepIndex;
        const isFuture = currentStepIndex >= 0 && idx > currentStepIndex;

        // Determine colors
        let bgColor = "bg-gray-700";
        let textColor = "text-gray-500";
        let borderColor = "border-transparent";

        if (isComplete) {
          bgColor = "bg-emerald-600/50";
          textColor = "text-emerald-300";
        } else if (isCurrent && isMyWork) {
          bgColor = "bg-indigo-600";
          textColor = "text-gray-900";
          borderColor = "border-indigo-400";
        } else if (isCurrent) {
          bgColor = "bg-blue-600/50";
          textColor = "text-blue-600";
        } else if (isMyWork) {
          bgColor = "bg-indigo-600/30";
          textColor = "text-indigo-300";
          borderColor = "border-indigo-500/50";
        }

        return (
          <div key={idx} className="flex items-center">
            {/* Step */}
            <div
              className={`
                px-2 py-1 rounded text-xs font-medium whitespace-nowrap
                border ${borderColor} ${bgColor} ${textColor}
                ${isCurrent ? "ring-2 ring-white/30" : ""}
              `}
              title={step.description || step.operationCode}
            >
              {step.operationCode || `Step ${step.step}`}
              {isComplete && (
                <svg
                  className="inline-block w-3 h-3 ml-1 text-emerald-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>

            {/* Arrow to next step */}
            {idx < routing.length - 1 && (
              <svg
                className={`w-4 h-4 mx-0.5 ${
                  isComplete ? "text-emerald-500/50" : "text-gray-600"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
}
